/**
 * AuditLog — /audit (TECH-28).
 *
 * Read-only chronological feed of every change to the action lifecycle.
 * Engine and user events appear in one stream; reading the same range
 * twice returns identical rows (server pagination, deterministic order).
 *
 * Out of scope (per ticket): CSV export, role-based gating.
 *
 * Filter set shipped in v1: date range, acting user, event type, source.
 * Per-category (action_type) and per-node (store/DC) filters are noted in
 * the Jira description but require backend support that isn't there yet
 * (they'd need new query params on /api/audit/actions). Tracked as
 * follow-ups.
 */
import { useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { motion } from 'framer-motion'
import {
  History, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight,
  Bot, User, Eye, Activity, Calendar, Info,
} from 'lucide-react'
import { useAuditEvents } from '../hooks/useAuditEvents.js'
import LoadingSpinner from '../components/shared/LoadingSpinner.jsx'
import Tooltip from '../components/shared/Tooltip.jsx'
import StatStrip from '../components/shared/StatStrip.jsx'
import CollapsibleFilterBar from '../components/shared/CollapsibleFilterBar.jsx'
import {
  EVENT_META, ENGINE_EVENT_TYPES, USER_EVENT_TYPES,
  NODE_TYPES, CATEGORIES,
} from '../utils/auditEvents.js'

const PAGE_SIZE = 50

// ── Helpers ─────────────────────────────────────────────────────────────────

function fmtCurrency(v) {
  if (v == null || v === '') return ''
  const n = Number(v)
  if (!Number.isFinite(n)) return ''
  const sign = n < 0 ? '-' : ''
  const abs = Math.abs(n)
  if (abs >= 1_000_000) return `${sign}$${(abs / 1_000_000).toFixed(2)}M`
  if (abs >= 1_000) return `${sign}$${(abs / 1_000).toFixed(1)}K`
  return `${sign}$${abs.toFixed(0)}`
}

function fmtAbsoluteTs(iso) {
  if (!iso) return '—'
  const d = new Date(iso)
  return d.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  })
}

function fmtActionDate(d) {
  if (!d) return null
  // Backend returns ISO date strings ('2026-05-30') from the DATE column.
  const dt = new Date(typeof d === 'string' ? d + 'T00:00:00Z' : d)
  if (Number.isNaN(dt.getTime())) return null
  return dt.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}

// Trim a yyyy-mm-dd back out of an ISO timestamp for <input type="date">.
function dateInput(iso) {
  if (!iso) return ''
  return iso.slice(0, 10)
}

function isoFromDateInput(dateStr, endOfDay = false) {
  if (!dateStr) return ''
  // Treat the date input as UTC midnight (start) or end-of-day (until filter).
  const suffix = endOfDay ? 'T23:59:59Z' : 'T00:00:00Z'
  return `${dateStr}${suffix}`
}

function actorLabel(ev) {
  if (ev.source === 'engine') return 'system'
  return ev.user_email || ev.user_name || '—'
}

// ── Preview cell — what to render in the right-hand column per event_type ──

function PayloadPreview({ ev }) {
  if (ev.source === 'engine') {
    const sev = ev.severity
    const pnl = ev.pnl_estimate
    return (
      <div className="flex flex-wrap items-center gap-2 text-xs text-slate-400">
        {sev != null && (
          <span className="inline-flex items-center gap-1">
            <span className="text-slate-500 uppercase tracking-wider text-[10px]">sev</span>
            <span className="text-slate-200 tabular-nums">{sev}</span>
          </span>
        )}
        {pnl != null && (
          <span className="inline-flex items-center gap-1">
            <span className="text-slate-500 uppercase tracking-wider text-[10px]">pnl</span>
            <span className="text-slate-200 tabular-nums">{fmtCurrency(pnl)}</span>
          </span>
        )}
        {ev.urgency && (
          <span className="text-[10px] uppercase tracking-wider text-slate-500">
            {ev.urgency.replaceAll('(', '').replaceAll(')', '')}
          </span>
        )}
      </div>
    )
  }
  // user events
  if (ev.event_type === 'value_set' && ev.value_usd != null) {
    return (
      <div className="text-sm text-violet-300 tabular-nums">
        {fmtCurrency(ev.value_usd)}
      </div>
    )
  }
  if (ev.comment) {
    return (
      <div className="text-sm text-slate-300 truncate max-w-full">{ev.comment}</div>
    )
  }
  return <div className="text-xs text-slate-500">—</div>
}

function EventBadge({ eventType }) {
  const m = EVENT_META[eventType] ?? {
    label: eventType, icon: Activity,
    color: 'text-slate-400', bg: 'bg-white/5', border: 'border-white/10',
  }
  const Icon = m.icon
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-medium border ${m.color} ${m.bg} ${m.border}`}>
      <Icon size={10} />
      {m.label}
    </span>
  )
}

function ActorChip({ source, label }) {
  const isSystem = source === 'engine'
  const Icon = isSystem ? Bot : User
  return (
    <span className={`inline-flex items-center gap-1.5 text-xs ${isSystem ? 'text-slate-400' : 'text-slate-200 mp-mask'}`}>
      <Icon size={11} className={isSystem ? 'text-slate-500' : 'text-slate-400'} />
      <span className={isSystem ? 'uppercase tracking-wider text-[10px]' : 'truncate max-w-[200px]'}>{label}</span>
    </span>
  )
}

// ── Main page ───────────────────────────────────────────────────────────────

export default function AuditLog() {
  const [params, setParams] = useSearchParams()

  // URL-driven state — everything that affects the request lives here so links
  // are shareable and reload-safe.
  const actionId   = params.get('action_id')   ?? ''
  const source     = params.get('source')      ?? ''   // '', 'engine', 'user'
  const eventType  = params.get('event_type')  ?? ''
  const userEmail  = params.get('user')        ?? ''
  const since      = params.get('since')       ?? ''   // 'YYYY-MM-DD'
  const until      = params.get('until')       ?? ''   // 'YYYY-MM-DD'
  const nodeType   = params.get('node_type')   ?? ''   // 'Store' | 'DC' | 'Lane' | 'Supplier'
  const nodeId     = params.get('node_id')     ?? ''   // substring match
  const category   = params.get('category')    ?? ''
  const page       = Math.max(1, Number(params.get('page')) || 1)

  const updateParam = (k, v) => {
    const next = new URLSearchParams(params)
    if (!v || v === '' || v === 'all') next.delete(k)
    else next.set(k, v)
    if (k !== 'page') next.delete('page')  // any filter change resets pagination
    setParams(next, { replace: true })
  }

  const { data, isLoading, isError, error, isFetching } = useAuditEvents({
    actionId: actionId || undefined,
    source: source || undefined,
    eventType: eventType || undefined,
    userEmail: userEmail || undefined,
    since: isoFromDateInput(since, false) || undefined,
    until: isoFromDateInput(until, true)  || undefined,
    nodeType: nodeType || undefined,
    nodeId: nodeId || undefined,
    category: category || undefined,
    page,
    pageSize: PAGE_SIZE,
  })

  const events = data?.events ?? []
  const total = data?.total ?? 0
  const totalPages = data?.total_pages ?? 1

  // ── Defensive UI dedup ──────────────────────────────────────────────────
  // The TECH-24 audit cleanup migration removes byte-identical 'changed'
  // rows in the database, but we also collapse consecutive identical
  // 'changed' events at render time. This catches anything a future engine
  // edge case might re-introduce (e.g. a brief NWS override toggle producing
  // two reads with the same final state).
  //
  // Dedup is intentionally scoped to 'changed' only — comments, value_set,
  // and lifecycle events (detected/closed/reopened/expired) are each
  // individually meaningful and never collapsed.
  const groupedEvents = useMemo(() => {
    const out = []
    let lastKey = null
    for (const ev of events) {
      const collapsible = ev.event_type === 'changed'
      const key = collapsible
        ? `${ev.action_id}|${ev.severity}|${ev.pnl_estimate}|${ev.urgency}|${ev.event_at}`
        : null
      if (key && key === lastKey && out.length > 0) {
        out[out.length - 1].duplicates += 1
      } else {
        out.push({ ev, duplicates: 0 })
        lastKey = key
      }
    }
    return out
  }, [events])

  const suppressedByDedup = events.length - groupedEvents.length

  // KPI counts come from the *current page* — for cross-page roll-ups the
  // backend would need a dedicated aggregator, which TECH-28 doesn't require.
  const stats = useMemo(() => {
    const distinct = new Set()
    let engineCount = 0
    let userCount = 0
    events.forEach((e) => {
      distinct.add(e.user_email || (e.source === 'engine' ? '__system__' : '__anon__'))
      if (e.source === 'engine') engineCount += 1
      else userCount += 1
    })
    return {
      total,
      pageRange: events.length ? `${(page - 1) * PAGE_SIZE + 1}–${(page - 1) * PAGE_SIZE + events.length}` : '0',
      distinct: distinct.size,
      engine: engineCount,
      user: userCount,
    }
  }, [events, total, page])

  const activeSummary = useMemo(() => {
    const bits = []
    if (actionId) bits.push(`Action: ${actionId.slice(0, 14)}…`)
    if (source) bits.push(source)
    if (eventType) bits.push(EVENT_META[eventType]?.label || eventType)
    if (userEmail) bits.push(userEmail)
    if (nodeType) bits.push(`Node: ${nodeType}`)
    if (nodeId) bits.push(`ID: ${nodeId}`)
    if (category) bits.push(category)
    if (since || until) bits.push(`${since || '…'} → ${until || '…'}`)
    return bits.join(' · ') || null
  }, [actionId, source, eventType, userEmail, nodeType, nodeId, category, since, until])

  return (
    <div className="flex flex-col min-h-[calc(100vh-4rem)] xl:h-[calc(100vh-4rem)] xl:overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-6 w-full flex-1 xl:flex xl:flex-col xl:min-h-0">
        <header className="mb-4 sm:mb-6">
          <h1 className="text-2xl font-black text-slate-100 flex items-center gap-2">
            <History size={20} className="text-cyan-400" />
            Audit
            <Tooltip content="Every engine and user change to the action lifecycle, newest first. Read-only." placement="bottom" asButton>
              <Info size={15} className="text-slate-500 hover:text-slate-300 cursor-help transition-colors" />
            </Tooltip>
          </h1>
        </header>

        <StatStrip
          items={[
            { label: 'Total events',  value: stats.total.toLocaleString(),    icon: History,    tone: 'slate' },
            { label: 'On this page',  value: stats.pageRange,                  icon: Eye,        tone: 'accent' },
            { label: 'Engine events', value: stats.engine.toLocaleString(),    icon: Bot,        tone: 'purple' },
            { label: 'User events',   value: stats.user.toLocaleString(),      icon: User,       tone: 'success' },
          ]}
        />

        <div className="mt-4">
          <CollapsibleFilterBar persistKey="aeo:filters:open:audit" activeSummary={activeSummary}>
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3">
              <FilterText
                label="Store / DC ID"
                value={nodeId}
                onChange={(v) => updateParam('node_id', v)}
                placeholder="1234, HZL, …"
              />
              <FilterSelect
                label="Node type"
                value={nodeType}
                onChange={(v) => updateParam('node_type', v)}
                options={[
                  { value: '', label: 'All nodes' },
                  ...NODE_TYPES.map((n) => ({ value: n, label: n })),
                ]}
              />
              <FilterSelect
                label="Category"
                value={category}
                onChange={(v) => updateParam('category', v)}
                options={[
                  { value: '', label: 'All categories' },
                  ...CATEGORIES.map((c) => ({ value: c, label: c })),
                ]}
              />
              <FilterText
                label="Action ID"
                value={actionId}
                onChange={(v) => updateParam('action_id', v)}
                placeholder="act_…"
              />
              <FilterSelect
                label="Source"
                value={source}
                onChange={(v) => updateParam('source', v)}
                options={[
                  { value: '', label: 'All sources' },
                  { value: 'engine', label: 'Engine (system)' },
                  { value: 'user', label: 'User' },
                ]}
              />
              <FilterSelect
                label="Event type"
                value={eventType}
                onChange={(v) => updateParam('event_type', v)}
                options={[
                  { value: '', label: 'All events' },
                  // group: engine
                  ...ENGINE_EVENT_TYPES.map((k) => ({ value: k, label: `engine · ${EVENT_META[k].label}` })),
                  // group: user
                  ...USER_EVENT_TYPES.map((k) => ({ value: k, label: `user · ${EVENT_META[k].label}` })),
                ]}
              />
              <FilterText
                label="User email"
                value={userEmail}
                onChange={(v) => updateParam('user', v)}
                placeholder="someone@…"
              />
              <DateRange
                since={since}
                until={until}
                onSinceChange={(v) => updateParam('since', v)}
                onUntilChange={(v) => updateParam('until', v)}
              />
            </div>
          </CollapsibleFilterBar>
        </div>

        <div className="mt-4 overflow-auto min-h-[300px] max-h-[min(70vh,700px)] xl:flex-1 xl:min-h-0 xl:max-h-none rounded-xl border border-white/5 bg-white/[0.02]">
          {isLoading && (
            <div className="p-10 flex items-center justify-center">
              <LoadingSpinner />
            </div>
          )}
          {isError && (
            <div className="p-10 text-center text-sm text-red-400">
              Failed to load audit events: {error?.message ?? 'Unknown error'}
            </div>
          )}
          {!isLoading && !isError && events.length === 0 && (
            <div className="p-10 text-center text-sm text-slate-500">
              No events match the current filters.
            </div>
          )}
          {!isLoading && !isError && events.length > 0 && (
            <>
              {suppressedByDedup > 0 && (
                <div className="px-4 py-2 text-[11px] text-slate-500 border-b border-white/5 bg-white/[0.015]">
                  Collapsed {suppressedByDedup} consecutive identical{' '}
                  <code className="text-slate-400">changed</code> event
                  {suppressedByDedup === 1 ? '' : 's'} for readability.
                </div>
              )}
              <div className="hidden md:block">
                <AuditTable groups={groupedEvents} />
              </div>
              <div className="md:hidden divide-y divide-white/5">
                {groupedEvents.map((g, i) => (
                  <AuditCard key={`${g.ev.ts}-${i}`} group={g} />
                ))}
              </div>
            </>
          )}
        </div>

        <Pager
          page={page}
          totalPages={totalPages}
          isFetching={isFetching}
          onChange={(p) => updateParam('page', String(p))}
        />
      </div>
    </div>
  )
}

// ── Filter controls ─────────────────────────────────────────────────────────

function FilterText({ label, value, onChange, placeholder }) {
  return (
    <div>
      <label className="block text-[10px] uppercase tracking-wider text-slate-500 mb-1">{label}</label>
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-sm text-slate-100 placeholder:text-slate-500 focus:outline-none focus:border-cyan-500/50 transition-colors"
      />
    </div>
  )
}

function FilterSelect({ label, value, onChange, options }) {
  return (
    <div>
      <label className="block text-[10px] uppercase tracking-wider text-slate-500 mb-1">{label}</label>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-sm text-slate-100 focus:outline-none focus:border-cyan-500/50 transition-colors"
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>{o.label}</option>
        ))}
      </select>
    </div>
  )
}

function DateRange({ since, until, onSinceChange, onUntilChange }) {
  return (
    <div>
      <label className="block text-[10px] uppercase tracking-wider text-slate-500 mb-1">Date range</label>
      <div className="flex items-center gap-1.5">
        <input
          type="date"
          value={since}
          onChange={(e) => onSinceChange(e.target.value)}
          className="flex-1 min-w-0 px-2 py-2 bg-white/5 border border-white/10 rounded-lg text-xs text-slate-100 focus:outline-none focus:border-cyan-500/50 transition-colors"
        />
        <span className="text-slate-500 text-xs">→</span>
        <input
          type="date"
          value={until}
          onChange={(e) => onUntilChange(e.target.value)}
          className="flex-1 min-w-0 px-2 py-2 bg-white/5 border border-white/10 rounded-lg text-xs text-slate-100 focus:outline-none focus:border-cyan-500/50 transition-colors"
        />
      </div>
    </div>
  )
}

// ── Table + card renderers ──────────────────────────────────────────────────

function DedupBadge({ count }) {
  if (!count || count < 1) return null
  return (
    <span
      className="inline-flex items-center px-1.5 py-0.5 rounded-md text-[9px] font-medium bg-slate-500/20 border border-slate-500/30 text-slate-300 whitespace-nowrap"
      title={`${count} consecutive identical update${count === 1 ? '' : 's'} hidden`}
    >
      +{count} similar
    </span>
  )
}

function AuditTable({ groups }) {
  return (
    <table className="w-full text-sm">
      <thead className="bg-white/5 sticky top-0 z-10">
        <tr>
          <th scope="col" className="text-left px-4 py-2.5 text-[10px] uppercase tracking-wider text-slate-500 font-medium w-44">Timestamp</th>
          <th scope="col" className="text-left px-4 py-2.5 text-[10px] uppercase tracking-wider text-slate-500 font-medium w-44">Actor</th>
          <th scope="col" className="text-left px-4 py-2.5 text-[10px] uppercase tracking-wider text-slate-500 font-medium w-40">Event</th>
          <th scope="col" className="text-left px-4 py-2.5 text-[10px] uppercase tracking-wider text-slate-500 font-medium">Action / Title</th>
          <th scope="col" className="text-left px-4 py-2.5 text-[10px] uppercase tracking-wider text-slate-500 font-medium">Detail</th>
        </tr>
      </thead>
      <tbody>
        {groups.map(({ ev, duplicates }, i) => (
          <motion.tr
            key={`${ev.ts}-${ev.action_id}-${ev.event_type}-${i}`}
            className="border-t border-white/5 hover:bg-white/[0.03] transition-colors"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.12, delay: Math.min(i, 10) * 0.005 }}
          >
            <td className="px-4 py-2.5 text-xs text-slate-300 whitespace-nowrap tabular-nums">{fmtAbsoluteTs(ev.ts)}</td>
            <td className="px-4 py-2.5"><ActorChip source={ev.source} label={actorLabel(ev)} /></td>
            <td className="px-4 py-2.5">
              <div className="flex items-center gap-1.5">
                <EventBadge eventType={ev.event_type} />
                <DedupBadge count={duplicates} />
              </div>
            </td>
            <td className="px-4 py-2.5">
              <div className="text-sm text-slate-200 truncate max-w-[380px]">{ev.title || '—'}</div>
              <div className="flex items-center gap-2 text-[10px] text-slate-500 mt-0.5">
                <span className="font-mono">{ev.action_id}</span>
                {ev.action_event_date && (
                  <span className="inline-flex items-center gap-1 text-slate-500">
                    <Calendar size={9} className="text-slate-600" />
                    target {fmtActionDate(ev.action_event_date)}
                  </span>
                )}
              </div>
            </td>
            <td className="px-4 py-2.5"><PayloadPreview ev={ev} /></td>
          </motion.tr>
        ))}
      </tbody>
    </table>
  )
}

function AuditCard({ group }) {
  const { ev, duplicates } = group
  return (
    <div className="px-4 py-3">
      <div className="flex items-start justify-between gap-2 mb-1.5">
        <div className="flex items-center gap-1.5">
          <EventBadge eventType={ev.event_type} />
          <DedupBadge count={duplicates} />
        </div>
        <span className="text-[10px] text-slate-500 tabular-nums whitespace-nowrap">{fmtAbsoluteTs(ev.ts)}</span>
      </div>
      <div className="text-sm text-slate-200 truncate mb-0.5">{ev.title || '—'}</div>
      <div className="flex items-center gap-2 text-[10px] text-slate-500 mb-1.5">
        <span className="font-mono">{ev.action_id}</span>
        {ev.action_event_date && (
          <span className="inline-flex items-center gap-1">
            <Calendar size={9} className="text-slate-600" />
            target {fmtActionDate(ev.action_event_date)}
          </span>
        )}
      </div>
      <div className="flex items-center justify-between gap-2">
        <ActorChip source={ev.source} label={actorLabel(ev)} />
        <PayloadPreview ev={ev} />
      </div>
    </div>
  )
}

function Pager({ page, totalPages, isFetching, onChange }) {
  if (totalPages <= 1) return (
    <div className="mt-3 text-right text-xs text-slate-500">
      {isFetching ? 'refreshing…' : ' '}
    </div>
  )
  const go = (p) => {
    const clamped = Math.max(1, Math.min(totalPages, p))
    if (clamped !== page) onChange(clamped)
  }
  return (
    <div className="mt-3 flex items-center justify-between text-xs text-slate-400">
      <div>{isFetching ? 'refreshing…' : ' '}</div>
      <div className="flex items-center gap-1">
        <PagerButton onClick={() => go(1)} disabled={page <= 1} icon={ChevronsLeft} label="First" />
        <PagerButton onClick={() => go(page - 1)} disabled={page <= 1} icon={ChevronLeft} label="Previous" />
        <div className="px-2 tabular-nums">Page {page} of {totalPages}</div>
        <PagerButton onClick={() => go(page + 1)} disabled={page >= totalPages} icon={ChevronRight} label="Next" />
        <PagerButton onClick={() => go(totalPages)} disabled={page >= totalPages} icon={ChevronsRight} label="Last" />
      </div>
    </div>
  )
}

function PagerButton({ onClick, disabled, icon: Icon, label }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={label}
      className="p-1.5 rounded-lg text-slate-400 hover:text-slate-200 hover:bg-white/5 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
    >
      <Icon size={14} />
    </button>
  )
}
